/**
 * Human-readable storefront product URLs: /product/coffee-table-a1b2c3d4
 * Legacy /product/<24-char hex> still resolves and is replaced in the address bar when possible.
 */

export function slugifyProductName(name) {
  return (
    String(name || 'product')
      .toLowerCase()
      .trim()
      .replace(/[^\w\s-]/g, '')
      .replace(/[\s_-]+/g, '-')
      .replace(/^-+|-+$/g, '') || 'product'
  );
}

/** Public segment used in /product/:segment */
export function getProductUrlSlugSegment(product) {
  if (!product) return '';
  const custom = product.urlSlug != null && String(product.urlSlug).trim();
  if (custom) return String(product.urlSlug).trim();
  const id = String(product.id || product._id || '');
  const base = slugifyProductName(product.name);
  if (!id) return base;
  const suffix = id.length >= 8 ? id.slice(-8).toLowerCase() : id.toLowerCase();
  return `${base}-${suffix}`;
}

export function getProductPath(product) {
  const seg = getProductUrlSlugSegment(product);
  if (!seg) return '/';
  return `/product/${encodeURIComponent(seg)}`;
}

export function isMongoObjectIdString(s) {
  return /^[a-f0-9]{24}$/i.test(String(s || '').trim());
}

export function findProductByUrlParam(products, param) {
  const raw = String(param || '').trim();
  if (!raw || !Array.isArray(products)) return null;
  let decoded = raw;
  try {
    decoded = decodeURIComponent(raw);
  } catch {
    decoded = raw;
  }
  if (isMongoObjectIdString(decoded)) {
    const byId = products.find((p) => String(p.id || p._id) === decoded);
    if (byId) return byId;
  }
  const bySlug = products.find(
    (p) =>
      (p.urlSlug && String(p.urlSlug).trim() === decoded) ||
      getProductUrlSlugSegment(p) === decoded
  );
  if (bySlug) return bySlug;

  const suffixMatch = decoded.match(/-([a-f0-9]{8})$/i);
  if (suffixMatch) {
    const suffix = suffixMatch[1].toLowerCase();
    const namePart = decoded.slice(0, decoded.length - suffix.length - 1).toLowerCase();
    const suffixMatches = products.filter((p) => {
      const pid = String(p.id || p._id || '');
      return pid.slice(-8).toLowerCase() === suffix;
    });
    if (suffixMatches.length === 1) return suffixMatches[0];
    if (suffixMatches.length > 1 && namePart) {
      const exact = suffixMatches.find((p) => slugifyProductName(p.name) === namePart);
      if (exact) return exact;
    }
  }

  return null;
}

/**
 * Rewrites <a href="/product/<24hex>"> to slug hrefs using the loaded catalog.
 */
export function rewriteLegacyProductAnchorHrefs(html, products) {
  if (!html || typeof html !== 'string' || !Array.isArray(products) || !products.length) return html;
  if (typeof DOMParser === 'undefined') return html;
  try {
    const parser = new DOMParser();
    const doc = parser.parseFromString(html, 'text/html');
    doc.querySelectorAll('a[href^="/product/"]').forEach((a) => {
      const href = a.getAttribute('href') || '';
      const m = href.match(/^\/product\/([^?#]+)/i);
      if (!m) return;
      let seg = m[1];
      try {
        seg = decodeURIComponent(seg);
      } catch {
        /* keep seg */
      }
      if (!isMongoObjectIdString(seg)) return;
      const p = findProductByUrlParam(products, seg);
      if (p) a.setAttribute('href', getProductPath(p));
    });
    return doc.body.innerHTML;
  } catch {
    return html;
  }
}
